// @ts-strict-ignore
import {
  ListboxButton,
  ListboxInput,
  ListboxList,
  ListboxOption,
  ListboxPopover,
  useListboxContext,
} from '@reach/listbox';
import { getCollisions } from '@reach/popover';
import type { PRect } from '@reach/rect';
import { useRect } from '@reach/rect';
import { useField } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import tw from 'twin.macro';
import { v4 as uuid } from 'uuid';

import type { VariantKeys } from '@/animation/common-variants';
import { variants } from '@/animation/common-variants';
import { AnimatedDownArrow } from '@/atoms/AnimatedArrow';
import { useBoop } from '@/hooks/useBoop';
import { useControlled } from '@/hooks/useControlled';
import { useDependencyCheck } from '@/hooks/useDependencyCheck';
import type { FormikFieldValiadtionType, PopoverPosition } from '@/types';
import { getPopoverPosition } from '@/utils/getPopoverPosition';
import { isString } from '@/utils/type-helpers';

type StylingProps = {
  /**
   * should the button have border
   */
  bordered?: boolean;
  /**
   * for full width listbox
   */
  fullWidth?: boolean;
  /**
   * should the selected label be highlighted
   */
  highlighted?: boolean;
  /**
   * is the listbox for phone panel?
   */
  isPhonePanel?: boolean;
  /**
   * is the listbox for app library searchbar?
   */
  isSearchbar?: boolean;
  /**
   * size of the listbox
   */
  size?: 'md' | 'sm' | 'xs';
};

export type ListBoxType<T extends Record<string, unknown>> = StylingProps & {
  /**
   * id of the animation variant
   */
  id?: string;
  /**
   *animation variant
   */
  animationVariant?: VariantKeys;
  /**
   * should show arrow
   */
  arrow?: boolean;
  /**
   * override css class
   */
  className?: string;
  /**
   * disable the listbox
   */
  disabled?: boolean;
  /**
   * disable portal for the list popover
   */
  disablePortal?: boolean | 'scroll';
  /**
   * default value, not required if `value` is specified
   */
  defaultValue?: string;
  /**
   * callback specifies the format of selected option, by default selected option label will be shown
   *
   * for Virtual list, seelcted value will be shown
   *
   * for this to work `selectedOption` callback should be passed
   */
  getOptionLabel?: (option: T) => React.ReactNode;
  /**
   * label describing the list box, will not be shown to the user
   */
  label: string;
  /**
   *
   */
  name?: string;
  /**
   * should the arrow animate?
   */
  noAnimation?: boolean;
  /**
   * blur callback
   */
  onBlur?: (event: React.FocusEvent) => void;
  /**
   * callback for controlled field
   */
  onChange?: (value: string) => void;
  /**
   * focus callback
   */
  onFocus?: (event: React.FocusEvent) => void;
  /**
   * list options
   */
  options: T[];
  /**
   * position of the popover
   */
  popoverPosition?: PopoverPosition;
  /**
   * callback to render the list option
   */
  renderOption: (option: T) => React.ReactNode;
  /**
   * callback to find the selected item from the list, required when `getOptionLabel` is passed
   */
  selectedOption?: (option: T, selectedValue: string) => boolean;
  /**
   * key which specifies the unique attribut of the passes options
   *
   * default is `id`
   */
  uniqueKey?: keyof T;
  /**
   * controlled list value
   */
  value?: string;
  /**
   * key which specifies the value attribut of the passes options
   *
   * default is `value`
   */
  valueKey?: keyof T;
  /**
   * should the list be virtualized?
   *
   * (use this option if the list size can go beyond 1000 items)
   */
  virtualized?: boolean;
  /**
   * to customise the height of the virtual list
   */
  virtualizedListHeight?: string;
  /**
   * to customise the width of the virtual list
   */
  virtualizedListWidth?: string;
};

const StyledListboxButton = styled(ListboxButton)<StylingProps>`
  ${tw`cursor-pointer bg-white rounded text-gray-900 transition-colors duration-300 hover:bg-gray-100 hover:disabled:bg-white aria-[disabled]:cursor-not-allowed aria-[disabled]:hover:bg-white pointer-events-auto focus-within:outline-none`}
  ${({ fullWidth }) => fullWidth && tw`w-full`}
  ${({ size }) =>
    size === 'xs'
      ? tw`pl-2 py-1 pr-1 body-xxs`
      : size === 'sm'
      ? tw`pl-3 py-1 pr-2 sub-heading-xs-bold`
      : tw`pl-4 py-2 pr-3 sub-heading-xs-bold`}
  ${({ bordered }) =>
    bordered
      ? tw`border border-solid border-gray-300 rounded`
      : tw`border-0! border-none`}
  ${({ isPhonePanel }) =>
    isPhonePanel &&
    tw`px-0 py-2 body-sm-bold bg-transparent hover:bg-transparent text-gray-500 rounded-lg`}
  ${({ highlighted }) => highlighted && tw`text-gray-900`}
  &[aria-expanded="true"] {
    ${tw`bg-gray-100`}

    & [data-reach-listbox-arrow] {
      ${tw`transform rotate-180`}
    }
  }
  &[data-focus-visible-added] {
    ${tw`ring-2 ring-gray-900`}
  }
  & [data-reach-listbox-arrow] {
    ${tw`!ml-1 inline-grid place-content-center transition-transform`}
  }
`;

const StyledList = styled(ListboxList)<{ virtualized?: boolean }>`
  ${tw`p-2 space-y-1`}
  ${({ virtualized }) => !virtualized && 'max-height: 20vh;'}
  min-width: 15ch;
  overflow: auto;
`;

const StyledPopover = styled(ListboxPopover)`
  ${tw`rounded !p-0 border border-solid border-gray-300 bg-white shadow-common-card focus-within:shadow-common-card focus-within:outline-none`}
  z-index: 1;
`;
const MotionPopover = motion(StyledPopover);

const StyledListItem = styled(ListboxOption)`
  ${tw`p-1 bg-white body-xs text-gray-900 rounded hover:bg-gray-100`}

  &[data-reach-listbox-option][aria-selected=true] {
    ${tw`bg-gray-100 text-gray-900`}
  }
`;

const Popover = <T extends Record<string, unknown>>({
  animationVariant,
  disablePortal,
  options,
  popoverPosition,
  renderOption,
  uniqueKey,
  valueKey,
  virtualized,
  virtualizedListHeight,
  virtualizedListWidth,
  targetRef,
}: Pick<
  ListBoxType<T>,
  | 'disablePortal'
  | 'options'
  | 'renderOption'
  | 'virtualized'
  | 'virtualizedListHeight'
  | 'virtualizedListWidth'
> &
  Required<
    Pick<
      ListBoxType<T>,
      'animationVariant' | 'popoverPosition' | 'uniqueKey' | 'valueKey'
    >
  > & {
    targetRef?: React.MutableRefObject<HTMLDivElement>;
  }) => {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const { isExpanded } = useListboxContext();
  const targetRect = useRect(targetRef, {
    observe: disablePortal && isExpanded,
  });
  const popoverRect = useRect(popoverRef, {
    observe: disablePortal && isExpanded,
  });

  return (
    <AnimatePresence>
      {isExpanded ? (
        <MotionPopover
          ref={popoverRef}
          portal={!disablePortal}
          {...(!disablePortal && {
            position: getPopoverPosition(popoverPosition),
          })}
          initial='initial'
          exit='exit'
          animate='animate'
          variants={variants[animationVariant]}
          {...(disablePortal && {
            style: {
              ...(popoverPosition === 'up' &&
                positionUp(targetRect, popoverRect)),
              position: 'absolute',
            },
          })}
        >
          {virtualized ? (
            <Virtuoso
              components={{
                Scroller: React.forwardRef(({ style, children }, ref) => (
                  <StyledList
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    ref={ref}
                    style={{
                      ...style,
                      height: virtualizedListHeight,
                      width: virtualizedListWidth,
                    }}
                    virtualized
                    {...{ children }}
                  />
                )),
              }}
              data={options}
              itemContent={(_, option) => {
                const value = option[valueKey];
                if (!isString(value)) {
                  throw new Error(
                    `Value key ${String(valueKey)} is not a string`
                  );
                }

                return (
                  <StyledListItem value={value}>
                    {renderOption(option)}
                  </StyledListItem>
                );
              }}
            />
          ) : null}
          {!virtualized && (
            <StyledList>
              {options.map((option) => {
                const key = option[uniqueKey];
                const value = option[valueKey];

                if (!isString(key)) {
                  throw new Error(
                    `Unique key ${String(uniqueKey)} is not a string`
                  );
                }
                if (!isString(value)) {
                  throw new Error(
                    `Value key ${String(valueKey)} is not a string`
                  );
                }

                return (
                  <StyledListItem key={key} value={value}>
                    {renderOption(option)}
                  </StyledListItem>
                );
              })}
            </StyledList>
          )}
        </MotionPopover>
      ) : null}
    </AnimatePresence>
  );
};

const ListBoxImpl = <T extends Record<string, unknown>>(
  {
    animationVariant = 'drop-in',
    arrow = true,
    bordered,
    className = '',
    disabled,
    disablePortal = false,
    fullWidth,
    defaultValue,
    getOptionLabel,
    highlighted,
    isPhonePanel,
    isSearchbar,
    label,
    name,
    noAnimation = true,
    onBlur,
    onChange,
    onFocus,
    options,
    popoverPosition = 'default',
    renderOption,
    selectedOption,
    size = 'md',
    uniqueKey = 'id',
    value,
    valueKey = 'value',
    virtualized = false,
    virtualizedListHeight = '25vh',
    virtualizedListWidth = '10vw',
    ...rest
  }: ListBoxType<T>,
  ref: React.Ref<HTMLDivElement>
): JSX.Element => {
  const targetRef = React.useRef<HTMLDivElement>(null);
  const { current: id } = React.useRef(uuid());
  const [props, trigger] = useBoop({ y: 4 });
  const [selectValue, setValue, isControlled] = useControlled<string>({
    controlled: value,
    default: String(defaultValue),
    name: 'ListBox',
  });
  const changeHandler = isControlled
    ? onChange
    : (value: string) => setValue(value);

  useDependencyCheck('ListBox', getOptionLabel, selectedOption);

  const selectedOptionValue = React.useMemo(() => {
    if (!getOptionLabel) {
      return selectValue;
    }

    if (!selectedOption) {
      throw new Error(
        'selectedOption prop is required when using getOptionLabel'
      );
    }

    const option = options.find(
      (option) => !!selectedOption(option, selectValue ?? '')
    );

    if (!option) {
      throw new Error(`for value ${selectValue} no option was found`);
    }

    return getOptionLabel(option);
  }, [getOptionLabel, options, selectValue, selectedOption]);

  return (
    <div ref={ref} {...rest}>
      {label ? (
        <div className='sr-only' id={id}>
          {label}
        </div>
      ) : null}
      <ListboxInput
        name={name}
        value={selectValue}
        onBlur={onBlur}
        onChange={changeHandler}
        onFocus={onFocus}
        className={className}
        disabled={disabled}
        {...(label && { 'aria-labelledby': id })}
        {...((disablePortal === 'scroll' ||
          (disablePortal && popoverPosition === 'up')) && {
          className: 'relative',
        })}
      >
        <StyledListboxButton
          ref={targetRef}
          isSearchbar={isSearchbar}
          onMouseEnter={() => (trigger as () => void)()}
          bordered={bordered}
          highlighted={highlighted}
          isPhonePanel={isPhonePanel}
          fullWidth={fullWidth}
          size={size}
          {...(arrow && {
            arrow: (
              <AnimatedDownArrow
                noAnimation={noAnimation}
                size='small'
                style={props as React.CSSProperties}
              />
            ),
          })}
        >
          <span>{selectedOptionValue}</span>
        </StyledListboxButton>
        <Popover
          options={options}
          renderOption={renderOption}
          animationVariant={animationVariant}
          disablePortal={disablePortal}
          uniqueKey={uniqueKey}
          valueKey={valueKey}
          popoverPosition={popoverPosition}
          virtualized={virtualized}
          virtualizedListHeight={virtualizedListHeight}
          virtualizedListWidth={virtualizedListWidth}
          targetRef={targetRef}
        />
      </ListboxInput>
    </div>
  );
};
export const ListBox = React.forwardRef(ListBoxImpl) as <
  T extends Record<string, unknown>
>(
  props: ListBoxType<T> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof ListBoxImpl>;

const FormikListboxImpl = <T extends Record<string, unknown>>(
  {
    name,
    validate,
    ...rest
  }: FormikFieldValiadtionType & ListBoxType<T> & { name: string },
  ref: React.Ref<HTMLDivElement>
) => {
  const [{ onChange, ...field }, , { setValue }] = useField({
    name,
    validate,
  });

  const changeHandler = (value: string) => {
    setValue(value);
  };

  return (
    <ListBox
      ref={ref}
      {...field}
      {...rest}
      name={name}
      onChange={changeHandler}
    />
  );
};
export const FormikListbox = React.forwardRef(FormikListboxImpl) as <
  T extends Record<string, unknown>
>(
  props: FormikFieldValiadtionType &
    ListBoxType<T> & {
      ref?: React.Ref<HTMLDivElement>;
    } & { name: string }
) => ReturnType<typeof FormikListboxImpl>;

const positionUp = (
  targetRect: PRect,
  popoverRect: PRect
): React.CSSProperties => {
  if (!targetRect || !popoverRect) {
    return {};
  }

  const { directionRight } = getCollisions(targetRect, popoverRect);

  return {
    [directionRight ? 'right' : 'left']: '0px',
    top: `-${popoverRect.height}px`,
  };
};
